/**
 * CustomerQuickAdd – Inline aanmaken van Klant.
 * Gebruikt in dropdowns waar een klant ontbreekt (offerte, project, etc.).
 * Behoudt parent form state; na succesvol aanmaken wordt het nieuwe item geselecteerd.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import api from '../../services/api'
import { useTouchedGuard } from '../../hooks/useUnsavedChanges'
import { klantFoutTekst } from '../../utils/klantFout'
import { duplicaatMelding } from '../../utils/klantDuplicaten'

const TYPES = [
  ['Particulier', 'klanten.particulier'],
  ['Zakelijk', 'klanten.zakelijk'],
  ['Architect', 'klanten.architect'],
  ['Aannemer', 'klanten.aannemer'],
  ['Projectontwikkelaar', 'klanten.projectontwikkelaar'],
]

const LEEG = {
  type_klant: 'Particulier',
  bedrijfsnaam: '',
  voornaam: '',
  achternaam: '',
  email: '',
  telefoon: '',
  straat: '',
  postcode: '',
  stad: '',
}

/**
 * @param {Object} props
 * @param {boolean} props.open - Modal open state
 * @param {function} props.onClose - Sluit modal
 * @param {function} props.onCreated - (klant: {id, naam, ...}) => void – selecteer nieuw item
 * @param {string} [props.invalidateKey] - Query key om te invalidaten (default: ['klanten'])
 */
export function CustomerQuickAdd({ open, onClose, onCreated, invalidateKey = ['klanten'] }) {
  const { t } = useTranslation()
  const guard = useTouchedGuard(open)
  const queryClient = useQueryClient()
  const [form, setForm] = useState(LEEG)

  const createMutation = useMutation({
    mutationFn: async (data) => {
      // De naam bepaalt de backend (naamregel in klant_service): hier alleen de ruwe velden.
      const payload = {
        type_klant: data.type_klant,
        bedrijfsnaam: data.type_klant !== 'Particulier' ? (data.bedrijfsnaam || null) : null,
        voornaam: data.voornaam || null,
        achternaam: data.achternaam || null,
        email: data.email || null,
        telefoon: data.telefoon || null,
        straat: data.straat || null,
        postcode: data.postcode || null,
        stad: data.stad || null,
        land: 'België',
        is_actief: true,
      }
      const res = await api.post('/klanten', payload)
      return res.data
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries(invalidateKey)
      onCreated?.({
        id: created.id,
        naam: created.naam,
        voornaam: created.voornaam,
        achternaam: created.achternaam,
      })
      onClose?.()
      toast.success(t('customers.created', 'Klant succesvol aangemaakt'))
      const melding = duplicaatMelding(t, created.mogelijke_duplicaten)
      if (melding) toast(melding, { icon: '⚠️', duration: 10000 })
      setForm(LEEG)
    },
    onError: (err) => {
      toast.error(klantFoutTekst(t, err, t('customers.createFailed', 'Fout bij aanmaken klant')))
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    createMutation.mutate(form)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" onClick={() => guard.closeWith(onClose)}>
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onChange={guard.onContentChange}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('customers.quickAdd', 'Nieuwe klant aanmaken')}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="quickadd-type" className="block text-sm font-medium text-gray-700 mb-1">{t('klanten.customerType', { defaultValue: 'Klanttype' })}</label>
            <select
              id="quickadd-type"
              value={form.type_klant}
              onChange={(e) => setForm((p) => ({ ...p, type_klant: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              autoFocus
            >
              {TYPES.map(([waarde, sleutel]) => (
                <option key={waarde} value={waarde}>{t(sleutel, { defaultValue: waarde })}</option>
              ))}
            </select>
          </div>
          {form.type_klant !== 'Particulier' && (
            <div>
              <label htmlFor="quickadd-bedrijfsnaam" className="block text-sm font-medium text-gray-700 mb-1">{t('klanten.fields.bedrijfsnaam', { defaultValue: 'Bedrijfsnaam' })} *</label>
              <input
                id="quickadd-bedrijfsnaam"
                type="text"
                value={form.bedrijfsnaam}
                onChange={(e) => setForm((p) => ({ ...p, bedrijfsnaam: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="quickadd-voornaam" className="block text-sm font-medium text-gray-700 mb-1">{t('klanten.fields.voornaam', { defaultValue: 'Voornaam' })}</label>
              <input
                id="quickadd-voornaam"
                type="text"
                value={form.voornaam}
                onChange={(e) => setForm((p) => ({ ...p, voornaam: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label htmlFor="quickadd-achternaam" className="block text-sm font-medium text-gray-700 mb-1">{t('klanten.fields.achternaam', { defaultValue: 'Achternaam' })}</label>
              <input
                id="quickadd-achternaam"
                type="text"
                value={form.achternaam}
                onChange={(e) => setForm((p) => ({ ...p, achternaam: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.email', 'E-mail')}</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('customers.telefoon', 'Telefoon')}</label>
            <input
              type="tel"
              value={form.telefoon}
              onChange={(e) => setForm((p) => ({ ...p, telefoon: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={() => guard.closeWith(onClose)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              {t('common.cancel', 'Annuleren')}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending ? t('common.saving', 'Opslaan...') : t('customers.create', 'Aanmaken')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
